import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import { DetailsList } from '#/components/DetailsList/DetailsList';
import { DetailsListItem } from '#/components/DetailsList/Item/DetailsListItem';
import { DetailListSection } from '#/components/DetailsList/Section/DetailListSection';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingIsoDateDetailsListItems } from '#/modules/listing/components/Date/ListingIsoDateDetailsListItems';
import type {
  IanaTimezone,
  DateString,
  TimeString,
} from '#/types/TypedDateString';
import { AREAS_OF_FOCUS } from '#/utils/constants/general/areasOfFocus';

export const formatAmountString = (amount: string) => {
  const parts = amount.split('.');
  const cents = parseInt(parts[1], 10);
  const [dollars] = parts;
  return `$${dollars}${cents > 0 ? `.${cents}` : ''}`;
};

type Props = {
  areasOfFocus: ApiAreaOfFocus[];
  startDate: DateString;
  startTime: TimeString;
  endDate: DateString | null;
  endTime: TimeString | null;
  timezone: IanaTimezone;
  feeAmount: string | null;
  feeRequired: boolean;
  welcomeGroups: boolean;
  welcomeFamilies: boolean;
  detailsWheelchairAccessible: boolean;
};

export function EventDetailsList({
  areasOfFocus,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
  feeAmount,
  feeRequired,
  welcomeGroups,
  welcomeFamilies,
  detailsWheelchairAccessible,
}: Props) {
  const { i18n } = useI18n();

  return (
    <DetailsList>
      <ListingIsoDateDetailsListItems
        startDate={startDate}
        startTime={startTime}
        endDate={endDate}
        endTime={endTime}
        timezone={timezone}
      />

      {feeRequired && (
        <DetailsListItem
          title={i18n('Cost')}
          iconName="coin"
          value={`${i18n(
            'Paid Event',
          )}${feeAmount ? `: ${formatAmountString(feeAmount)}` : ''}`}
        />
      )}

      {areasOfFocus?.length !== 0 && (
        <DetailsListItem
          iconName="globe"
          title={i18n('Cause Areas')}
          value={areasOfFocus
            .map((item) => AREAS_OF_FOCUS(i18n)[item])
            .join(', ')}
        />
      )}

      <DetailListSection title={i18n('Participation')}>
        {welcomeGroups && (
          <DetailsListItem iconName="group" value={i18n('Groups Welcome')} />
        )}

        {welcomeFamilies && (
          <DetailsListItem iconName="family" value={i18n('Families Welcome')} />
        )}

        {detailsWheelchairAccessible && (
          <DetailsListItem
            iconName="wheelchair"
            value={i18n('Wheelchair Accessible')}
          />
        )}
      </DetailListSection>
    </DetailsList>
  );
}
